import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Loader2,
  Package,
  AlertCircle,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";

import { warehouseLocationsApi } from "../services/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useModal } from "../hooks/useModal";

const EMPTY_FORM = {
  client_center_id: "",
  name: "",
  address: "",
  postal_code: "",
  is_active: true,
};

const WarehouseLocationsManagement = () => {
  const { translate } = useLanguage();

  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedForStock, setSelectedForStock] = useState(null);
  const [productIdInput, setProductIdInput] = useState("");
  const [stockResult, setStockResult] = useState(null);
  const [checkingStock, setCheckingStock] = useState(false);

  const fetchWarehouses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setWarehouses(await warehouseLocationsApi.getAllWarehouses(includeInactive));
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.message ||
        translate("warehousesFetchError") ||
        "Error fetching warehouses";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [includeInactive, translate]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setSelectedWarehouse(null);
  };

  // Fallbacks are English sentences, never the key: the repo idiom everywhere is
  // `translate(key) || "English fallback"`, and a missing translation must not show
  // the user `warehousesCreateSuccess`.
  const run = async (action, success, error, onDone) => {
    try {
      setLoading(true);
      await action();
      toast.success(translate(success.key) || success.fallback);
      onDone?.();
      await fetchWarehouses();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || err.message || translate(error.key) || error.fallback
      );
    } finally {
      setLoading(false);
    }
  };

  // Empty strings become null, the same shape handleUpdate sends. Without this the
  // two paths store different things for "no address" and the API's own test for the
  // optional fields asserts a state the UI could never produce.
  const payloadFrom = (form) => ({
    ...form,
    client_center_id: form.client_center_id.trim(),
    name: form.name.trim(),
    address: form.address.trim() || null,
    postal_code: form.postal_code.trim() || null,
  });

  // The `required` on the two inputs never runs: they are not inside a <form> and Save
  // is a plain button, so a blank Save used to POST empty strings. Every route that
  // addresses one warehouse is `/{client_center_id}`, so an empty code produced a row
  // no UI action could reach again — the path collapsed onto the collection route and
  // delete, edit and toggle answered 405. SQL was the only way out. The backend now
  // refuses it too; this is the half that stops the user ever getting there.
  const requiredFieldsFilled =
    formData.client_center_id.trim() !== "" && formData.name.trim() !== "";

  const handleCreate = () =>
    run(
      () => warehouseLocationsApi.createWarehouse(payloadFrom(formData)),
      { key: "warehousesCreateSuccess", fallback: "Warehouse created" },
      { key: "warehousesCreateError", fallback: "Error creating warehouse" },
      () => {
        setShowCreateModal(false);
        resetForm();
      }
    );

  const handleUpdate = () =>
    run(
      () =>
        warehouseLocationsApi.updateWarehouse(selectedWarehouse.client_center_id, {
          name: formData.name,
          address: formData.address || null,
          postal_code: formData.postal_code || null,
          is_active: formData.is_active,
        }),
      { key: "warehousesUpdateSuccess", fallback: "Warehouse updated" },
      { key: "warehousesUpdateError", fallback: "Error updating warehouse" },
      () => {
        setShowEditModal(false);
        resetForm();
      }
    );

  const handleDelete = (warehouse) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(translate("warehousesDeleteConfirm") || "Remove this warehouse?")) return;
    return run(
      () => warehouseLocationsApi.deleteWarehouse(warehouse.client_center_id),
      { key: "warehousesDeleteSuccess", fallback: "Warehouse removed" },
      { key: "warehousesDeleteError", fallback: "Error removing warehouse" }
    );
  };

  const handleToggleActive = (warehouse) =>
    run(
      () => warehouseLocationsApi.toggleWarehouseActive(warehouse.client_center_id),
      { key: "warehousesUpdateSuccess", fallback: "Warehouse updated" },
      { key: "warehousesUpdateError", fallback: "Error updating warehouse" }
    );

  const openEdit = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setFormData({
      client_center_id: warehouse.client_center_id,
      name: warehouse.name,
      address: warehouse.address || "",
      postal_code: warehouse.postal_code || "",
      is_active: warehouse.is_active,
    });
    setShowEditModal(true);
  };

  // `checkingStock` is reset in BOTH of these. Leaving it set survived a close and
  // carried into the next row's modal, which opened with the input disabled and the
  // button reading "Checking..." for a check nobody had started.
  const openStock = (warehouse) => {
    setSelectedForStock(warehouse);
    setProductIdInput("");
    setStockResult(null);
    setCheckingStock(false);
    setShowStockModal(true);
  };

  const closeStock = () => {
    setShowStockModal(false);
    setSelectedForStock(null);
    setProductIdInput("");
    setStockResult(null);
    setCheckingStock(false);
  };

  const stockModalRef = useModal(showStockModal, closeStock);

  const handleCheckStock = async () => {
    if (!productIdInput.trim()) return;
    // Captured before the await. A slow SAP call that lands after the user has moved
    // to the next row used to render the previous centre's number under the new
    // centre's header, and the only tell was the Name line above it. On the one screen
    // whose whole purpose is telling a typo apart from a real-but-empty code, a
    // wrong-row number is the single answer that must not happen.
    const askedFor = selectedForStock.client_center_id;
    setCheckingStock(true);
    setStockResult(null);
    try {
      const result = await warehouseLocationsApi.checkWarehouseStock(
        askedFor,
        productIdInput.trim()
      );
      setSelectedForStock((current) => {
        if (current?.client_center_id === askedFor) setStockResult(result);
        return current;
      });
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          err.message ||
          translate("warehousesStockError") ||
          "Error checking stock"
      );
    } finally {
      setCheckingStock(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {translate("warehousesTitle") || "Warehouses and Logistics Centre"}
        </h2>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>{translate("warehousesAddWarehouse") || "Add Warehouse"}</span>
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="w-4 h-4 text-[var(--color-mint)] rounded focus:ring-[var(--color-mint)]"
          />
          <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
            {translate("warehousesIncludeInactive") || "Include inactive"}
          </span>
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 rounded-xl flex items-center space-x-3" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
          <AlertCircle className="flex-shrink-0" size={20} style={{ color: "var(--color-error)" }} />
          <p style={{ color: "var(--color-error)" }}>{error}</p>
        </div>
      )}

      {/* Loading State - Skeleton */}
      {loading && (
        <div className="overflow-x-auto animate-pulse">
          <div className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
              <div className="grid grid-cols-6 gap-4">
                <div className="h-5 w-20 rounded bg-[var(--color-skeleton)]" />
                <div className="h-5 w-24 rounded bg-[var(--color-skeleton)]" />
                <div className="h-5 w-32 rounded bg-[var(--color-skeleton)]" />
                <div className="h-5 w-24 rounded bg-[var(--color-skeleton)]" />
                <div className="h-5 w-16 rounded bg-[var(--color-skeleton)]" />
                <div className="h-5 w-20 rounded bg-[var(--color-skeleton)] ml-auto" />
              </div>
            </div>
            <div className="divide-y divide-[var(--color-border)]">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="px-4 py-3">
                  <div className="grid grid-cols-6 gap-4 items-center">
                    <div className="h-4 w-20 rounded bg-[var(--color-skeleton-light)]" />
                    <div className="h-4 w-32 rounded bg-[var(--color-skeleton-light)]" />
                    <div className="h-4 w-40 rounded bg-[var(--color-skeleton-light)]" />
                    <div className="h-4 w-24 rounded bg-[var(--color-skeleton-light)]" />
                    <div className="h-6 w-16 rounded-full bg-[var(--color-skeleton-light)]" />
                    <div className="flex items-center justify-end space-x-2">
                      <div className="w-8 h-8 rounded bg-[var(--color-skeleton-light)]" />
                      <div className="w-8 h-8 rounded bg-[var(--color-skeleton-light)]" />
                      <div className="w-8 h-8 rounded bg-[var(--color-skeleton-light)]" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Warehouses Table */}
      {!loading && warehouses.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr style={{ backgroundColor: "var(--color-surface-raised)" }} className="border-b border-[var(--color-border)]">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                  {translate("warehousesCenterId") || "SAP Code"}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                  {translate("warehousesName") || "Name"}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                  {translate("warehousesAddress") || "Address"}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                  {translate("warehousesPostalCode") || "Postal Code"}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                  {translate("warehousesActive") || "Active"}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                  {translate("storeLocationsTableActions") || "Actions"}
                </th>
              </tr>
            </thead>
            <tbody>
              {warehouses.map((warehouse) => (
                <tr
                  key={warehouse.client_center_id}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-raised)]"
                >
                  <td className="px-4 py-3 text-sm" style={{ color: "var(--color-text-primary)" }}>
                    {warehouse.client_center_id}
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: "var(--color-text-primary)" }}>
                    {warehouse.name}
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    {warehouse.address}
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    {warehouse.postal_code}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        warehouse.is_active ? "badge-active" : "badge-inactive"
                      }`}
                    >
                      {warehouse.is_active
                        ? translate("active") || "Active"
                        : translate("inactive") || "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openStock(warehouse)}
                        className="p-2 rounded transition-colors hover:bg-[var(--color-mint-muted)]"
                        style={{ color: "var(--color-text-muted)" }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-mint)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}
                        title={translate("warehousesCheckStock") || "Check stock"}
                      >
                        <Package size={18} />
                      </button>
                      <button
                        onClick={() => handleToggleActive(warehouse)}
                        className="p-2 rounded transition-colors hover:bg-[var(--color-mint-muted)]"
                        style={{ color: "var(--color-text-muted)" }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-mint)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}
                        title={
                          warehouse.is_active
                            ? translate("storeLocationsDeactivate") || "Deactivate"
                            : translate("storeLocationsActivate") || "Activate"
                        }
                      >
                        {warehouse.is_active ? <PowerOff size={18} /> : <Power size={18} />}
                      </button>
                      <button
                        onClick={() => openEdit(warehouse)}
                        className="p-2 rounded transition-colors hover:bg-[var(--color-mint-muted)]"
                        style={{ color: "var(--color-text-muted)" }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-mint)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}
                        title={translate("edit") || "Edit"}
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(warehouse)}
                        className="p-2 rounded transition-colors"
                        style={{ color: "var(--color-text-muted)" }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-error)"; e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.08)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; e.currentTarget.style.backgroundColor = "transparent"; }}
                        title={translate("delete") || "Delete"}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && warehouses.length === 0 && (
        <div className="text-center py-12">
          <Package className="mx-auto mb-4" size={48} style={{ color: "var(--color-text-muted)" }} />
          <p style={{ color: "var(--color-text-secondary)" }}>
            {translate("warehousesEmpty") || "No warehouses configured yet"}
          </p>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <WarehouseModal
          title={translate("warehousesAddWarehouse") || "Add Warehouse"}
          formData={formData}
          setFormData={setFormData}
          onSave={handleCreate}
          onCancel={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          loading={loading}
          canSave={requiredFieldsFilled}
          isCreate={true}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedWarehouse && (
        <WarehouseModal
          title={translate("warehousesEditWarehouse") || "Edit Warehouse"}
          formData={formData}
          setFormData={setFormData}
          onSave={handleUpdate}
          onCancel={() => {
            setShowEditModal(false);
            resetForm();
          }}
          loading={loading}
          canSave={requiredFieldsFilled}
          isCreate={false}
        />
      )}

      {/* Stock Modal */}
      {showStockModal && selectedForStock && (
        <WarehouseStockModal
          warehouse={selectedForStock}
          productId={productIdInput}
          setProductId={setProductIdInput}
          stockResult={stockResult}
          checkingStock={checkingStock}
          onCheck={handleCheckStock}
          onClose={closeStock}
          modalRef={stockModalRef}
        />
      )}
    </div>
  );
};

const WarehouseModal = ({
  title,
  formData,
  setFormData,
  onSave,
  onCancel,
  loading,
  canSave,
  isCreate,
}) => {
  const { translate } = useLanguage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: "var(--color-surface)" }}>
        <div className="p-6 border-b border-[var(--color-border)]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>{title}</h2>
            <button
              onClick={onCancel}
              style={{ color: "var(--color-text-muted)" }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {isCreate && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("warehousesCenterId") || "SAP Code"} *
              </label>
              <input
                type="text"
                value={formData.client_center_id}
                onChange={(e) =>
                  setFormData({ ...formData, client_center_id: e.target.value })
                }
                className="field-input w-full"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("warehousesName") || "Name"} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="field-input w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("warehousesAddress") || "Address"}
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="field-input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("warehousesPostalCode") || "Postal Code"}
            </label>
            <input
              type="text"
              value={formData.postal_code}
              onChange={(e) =>
                setFormData({ ...formData, postal_code: e.target.value })
              }
              className="field-input w-full"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="w-4 h-4 text-[var(--color-mint)] rounded focus:ring-[var(--color-mint)]"
              />
              <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                {translate("warehousesActive") || "Active"}
              </span>
            </label>
          </div>
        </div>

        <div className="p-6 border-t border-[var(--color-border)] flex items-center justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="btn-secondary disabled:opacity-50"
          >
            {translate("cancel") || "Cancel"}
          </button>
          <button
            onClick={onSave}
            disabled={loading || !canSave}
            className="btn-primary disabled:opacity-50 flex items-center space-x-2"
          >
            {loading && <Loader2 className="animate-spin" size={16} />}
            <span>{translate("save") || "Save"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const WarehouseStockModal = ({
  warehouse,
  productId,
  setProductId,
  stockResult,
  checkingStock,
  onCheck,
  onClose,
  modalRef,
}) => {
  const { translate } = useLanguage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: "var(--color-surface)" }}
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
          <h2 className="text-xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
            {translate("warehousesCheckStock") || "Check stock"}
          </h2>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: "var(--color-text-muted)" }}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <p className="text-lg font-medium" style={{ color: "var(--color-text-primary)" }}>{warehouse.name}</p>
            <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
              {warehouse.client_center_id}
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-secondary)" }}>
              {translate("warehousesStockProductId") || "Product SAP Code"} *
            </label>
            <input
              type="text"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !checkingStock) {
                  onCheck();
                }
              }}
              className="field-input w-full"
              disabled={checkingStock}
            />
          </div>

          <button
            onClick={onCheck}
            disabled={checkingStock || !productId.trim()}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {checkingStock ? (
              <>
                <Loader2 className="animate-spin mr-2" size={18} />
                {translate("storeStockChecking") || "Checking..."}
              </>
            ) : (
              translate("storeStockCheckButton") || "Check"
            )}
          </button>

          {stockResult && (
            <div className="mt-6 p-4 bg-[var(--color-surface-raised)] rounded-xl border border-[var(--color-border)]">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--color-text-muted)" }}>
                {translate("storeStockResult") || "Result"}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("storeStockProductId") || "Product"}
                  </span>
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {stockResult.product_id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("warehousesName") || "Name"}
                  </span>
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {stockResult.warehouse_name}
                  </span>
                </div>
                {/* A quantidade imprime-se sempre, inclusive zero: os armazens
                    regionais tem menos de 2% do catalogo cada, logo 0 e a
                    resposta certa e frequente. Uma guarda `&&` aqui escondia o
                    numero exactamente onde a funcionalidade esta a funcionar. */}
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("warehousesStockResult") || "Units in stock"}
                  </span>
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {stockResult.quantity}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarehouseLocationsManagement;
